import { getSimpleDayTemplates, populateSimpleDayTemplate } from '@go-client/lib/api/template';
import { Property } from '@go-shared/types/property';
import {
  BareSimpleSailing,
  BareSimpleSailingDay,
  BareSimpleSailingDayBundle,
} from '@go-shared/types/sailing';
import { SimpleTemplate } from '@go-shared/types/template';
import {
  LinkFragment,
  StrategicLinkFragment,
  TemplateFragmentType,
} from '@go-shared/types/templateFragment';
import React, { useEffect, useState } from 'react';

import { BubbleForm, BubbleOverlay } from '../BubbleUI';
import { BubbleFormPage } from '../BubbleUI/BubbleForm';
import DaySummaryPage from './DaySummaryPage';
import SailingSummaryPage from './SailingSummaryPage';
import { TemplateMappingPage } from './TemplateMappingPage';
import TemplateSelectPage from './TemplateSelectPage';
import { User } from '@go-shared/types/user';
import { safeIdCompare } from '@go-shared/lib/utils';
import { dateToIsoUTC } from '@go-shared/lib/time';

export interface FragmentBlockSync {
  type: TemplateFragmentType;
  text: string;
  templateId: string;
  active: boolean;
}

interface TemplateLinkFormPropsBase {
  property: Property;
  user: User;
  templates?: SimpleTemplate[];
  onClose?: () => void;
  onTemplateNameClick?: (blockSync: FragmentBlockSync) => void;
  onUnlinkTemplateFragments?: (linkFragments: StrategicLinkFragment[]) => Promise<void>;
  onLinkTemplateFragments?: (linkFragments: LinkFragment[]) => Promise<void>;
  onSailingDayNameClick?: (sailing: BareSimpleSailing, sailingDay: BareSimpleSailingDay) => void;
  onCreateTemplateClick?: (sailing: BareSimpleSailing, sailingDay: BareSimpleSailingDay) => void;
}

interface TemplateLinkFormSingleMode {
  mode: 'single';
  sailing: BareSimpleSailing;
  sailingDay: BareSimpleSailingDay;
}

interface TemplateLinkFormBulkMode {
  mode: 'bulk';
  sailing: BareSimpleSailing;
}

interface TemplateLinkFormMegaBulkMode {
  mode: 'megaBulk';
}

export type TemplateLinkFormProps = TemplateLinkFormPropsBase &
  (TemplateLinkFormSingleMode | TemplateLinkFormBulkMode | TemplateLinkFormMegaBulkMode);

export default function TemplateLinkForm(props: TemplateLinkFormProps) {
  const [templates, setTemplates] = useState<SimpleTemplate[] | undefined>(props.templates);
  const [selectedTemplates, setSelectedTemplates] = useState<SimpleTemplate[]>([]);

  useEffect(() => {
    fetchTemplates().catch(console.error);
  }, [props.property, props.templates]);

  async function fetchTemplates() {
    const tmpls =
      props.templates ||
      (await getSimpleDayTemplates(props.property._id)).map(t =>
        populateSimpleDayTemplate(t, props.property)
      );
    setTemplates(tmpls);
  }

  let sailingBundles: BareSimpleSailingDayBundle[] = [];

  switch (props.mode) {
    case 'single':
      sailingBundles = [
        {
          sailing: props.sailing,
          day: props.sailingDay,
          dayIndex: props.sailing.days.findIndex(d => safeIdCompare(d._id, props.sailingDay._id)),
          dateIso: dateToIsoUTC(props.sailingDay.date),
        },
      ];
      break;
    case 'bulk':
      sailingBundles = props.sailing.days.map((d, i) => ({
        sailing: props.sailing,
        day: d,
        dayIndex: i,
        dateIso: dateToIsoUTC(d.date),
      }));
      break;
    case 'megaBulk':
      break;
  }

  let title: string | undefined;
  const pages: Array<BubbleFormPage<unknown>> = [];

  if (props.mode === 'bulk') {
    title = 'Bulk Link Template Fragments';

    pages.push({
      key: 'page-2',
      render: actions =>
        sailingBundles.length > 0 ? (
          <SailingSummaryPage
            user={props.user}
            sailing={sailingBundles[0].sailing}
            templates={templates || []}
            onTemplateNameClick={props.onTemplateNameClick}
            onSailingDayNameClick={props.onSailingDayNameClick}
            onUnlinkTemplateFragments={props.onUnlinkTemplateFragments}
            onCreateTemplateClick={props.onCreateTemplateClick}
            onClose={props.onClose}
            onGoNext={actions.goForward}
          />
        ) : (
          <span>Cannot load this component due to missing data</span>
        ),
    });
  } else if (props.mode === 'single') {
    pages.push({
      key: 'page-3',
      render: actions =>
        sailingBundles.length === 1 ? (
          <DaySummaryPage
            user={props.user}
            sailing={sailingBundles[0].sailing}
            sailingDay={sailingBundles[0].day}
            templates={templates || []}
            onClose={props.onClose}
            onUnlinkTemplateFragments={props.onUnlinkTemplateFragments}
            onTemplateNameClick={props.onTemplateNameClick}
            onSailingDayNameClick={props.onSailingDayNameClick}
            onCreateTemplateClick={props.onCreateTemplateClick}
            onGoNext={actions.goForward}
          />
        ) : (
          <span>Cannot load this component due to missing data</span>
        ),
    });
  }

  pages.push(
    {
      key: 'page-4',
      render: actions => (
        <TemplateSelectPage
          title={title}
          property={props.property}
          sailingBundles={sailingBundles}
          templates={templates || []}
          selectedTemplates={selectedTemplates}
          onClose={props.onClose}
          onSelectedTemplatesChange={tmpls => setSelectedTemplates(tmpls)}
          onGoBack={actions.goBack}
          onGoNext={actions.goForward}
        />
      ),
    },
    {
      key: 'page-5',
      render: actions => (
        <TemplateMappingPage
          title={title}
          property={props.property}
          sailingBundles={sailingBundles}
          templates={templates || []}
          selectedTemplates={selectedTemplates}
          onClose={props.onClose}
          onFinishLinkTemplateFragments={props.onLinkTemplateFragments}
          onGoBack={actions.goBack}
        />
      ),
    }
  );

  return (
    <div className="template-fragment-link-form">
      {!templates ? (
        <BubbleOverlay hideBg style={{ minHeight: '500px' }} loading={!templates} />
      ) : (
        <BubbleForm pages={pages} />
      )}
    </div>
  );
}
